import { useCallback, useEffect, useRef, useState } from "react";

const SEARCH_DEBOUNCE_MS = 250;

/**
 * Drives a paginated, searchable list backed by a DRF list endpoint.
 *
 * Returns the current page of rows plus the controls the page needs. Responses
 * that arrive after a newer request are discarded, so fast typing cannot leave
 * stale rows on screen.
 */
export function useResourceList(resource) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ rows: [], count: 0, isLoading: true, error: null });

  const requestId = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status]);

  const load = useCallback(async () => {
    const currentRequest = ++requestId.current;
    setState((previous) => ({ ...previous, isLoading: true, error: null }));

    try {
      const data = await resource.list({
        page,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(status ? { status } : {}),
      });

      if (currentRequest === requestId.current) {
        setState({ rows: data.results, count: data.count, isLoading: false, error: null });
      }
    } catch (error) {
      if (currentRequest === requestId.current) {
        setState({ rows: [], count: 0, isLoading: false, error: error.message });
      }
    }
  }, [resource, page, debouncedSearch, status]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    ...state,
    page,
    setPage,
    search,
    setSearch,
    status,
    setStatus,
    reload: load,
  };
}
